import React, { useState, useRef } from 'react';
import { useSlideshow } from '../../contexts/SlideshowContext';
import './SlideshowManager.css';

function SlideshowManager() {
  const { slides, loading, error, addSlide, editSlide, deleteSlide } = useSlideshow();
  const [isAddingSlide, setIsAddingSlide] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  
  const fileInputRef = useRef(null);
  
  const handleAddNewClick = () => {
    setIsAddingSlide(true);
    setEditingSlide(null);
    resetForm();
  };
  
  const handleEditClick = (slide) => {
    setIsAddingSlide(false);
    setEditingSlide(slide);
    setTitle(slide.title || '');
    setDescription(slide.description || '');
    setImagePreview(slide.imageBase64 || '');
    setImage(null);
  };
  
  const handleDeleteClick = async (slide) => {
    if (window.confirm(`Are you sure you want to delete this slide?`)) {
      setIsLoading(true);
      setActionError('');
      setActionSuccess('');
      
      try {
        await deleteSlide(slide.id);
        setActionSuccess('Slide deleted successfully!');
      } catch (err) {
        setActionError(err.message || 'Failed to delete slide');
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  const handleToggleActive = async (slide) => {
    setIsLoading(true);
    setActionError('');
    setActionSuccess('');
    
    try {
      await editSlide(slide.id, { active: !slide.active });
      setActionSuccess(`Slide ${slide.active ? 'deactivated' : 'activated'} successfully!`);
    } catch (err) {
      setActionError(err.message || 'Failed to update slide');
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setImage(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleCancel = () => {
    setIsAddingSlide(false);
    setEditingSlide(null);
    resetForm();
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setActionError('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
      return;
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setActionError('Image file is too large. Maximum size is 5MB.');
      return;
    }
    
    setImage(file);
    
    // Create a preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setActionError('');
    setActionSuccess('');
    
    try {
      if (editingSlide) {
        // Edit existing slide
        await editSlide(editingSlide.id, { 
          title, 
          description, 
          imageFile: image 
        });
        setActionSuccess('Slideshow saved successfully!');
        
        // Auto close form after 1.5 seconds on successful edit
        setTimeout(() => {
          setIsAddingSlide(false);
          setEditingSlide(null);
        }, 1500);
      } else {
        // Add new slide
        if (!image) {
          throw new Error('Please select an image for the slide');
        }
        
        await addSlide(title, description, image);
        setActionSuccess('Slideshow saved successfully!');
        resetForm();
        
        // Auto close form after 1.5 seconds on successful add
        setTimeout(() => {
          setIsAddingSlide(false);
        }, 1500);
      }
    } catch (err) {
      setActionError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="slideshow-manager">
        <h2 className="section-title">Slideshow Manager</h2>
        <div className="loading-indicator">Loading slideshow data...</div>
      </div>
    );
  }
  
  return (
    <div className="slideshow-manager">
      <h2 className="section-title">Slideshow Manager</h2>
      
      {error && <div className="error-message">{error}</div>}
      {actionError && <div className="error-message">{actionError}</div>}
      {actionSuccess && <div className="success-message">{actionSuccess}</div>}
      
      {!isAddingSlide && !editingSlide && (
        <button 
          className="add-slide-btn"
          onClick={handleAddNewClick}
          disabled={isLoading}
          style={{
            background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
            color: 'white',
            fontSize: '1.1rem',
            padding: '1rem 2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            borderRadius: '12px',
            fontWeight: '700',
            boxShadow: '0 8px 20px rgba(56, 239, 125, 0.25)',
            border: 'none',
            cursor: 'pointer',
            marginBottom: '2rem',
            transition: 'all 0.3s ease'
          }}
        >
          <span style={{ fontSize: '1.4rem' }}>➕</span>
          Post New Slideshow
        </button>
      )}
      
      {(isAddingSlide || editingSlide) && (
        <div className="slide-form-container">
          <h3>{editingSlide ? 'Edit Slide' : 'Add New Slide'}</h3>
          
          <form onSubmit={handleSubmit} className="slide-form">
            <div className="form-group">
              <label htmlFor="title">Title (Optional)</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter slide title"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter slide description"
                rows="3"
              />
            </div>
            
            <div className="form-group">
              <label>Slide Image {!editingSlide && <span className="required">*</span>}</label>
              
              <div className="image-upload-controls">
                <input
                  type="file"
                  id="slide-image"
                  ref={fileInputRef}
                  accept="image/jpeg, image/png, image/gif, image/webp"
                  onChange={handleImageChange}
                  className="image-file-input"
                />
                <label htmlFor="slide-image" className="image-upload-button">
                  {editingSlide ? 'Change Image' : 'Select Image'}
                </label>
              </div>
              
              {imagePreview && (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                  <p className="image-info">
                    {image ? `Selected: ${image.name} (${(image.size / 1024 / 1024).toFixed(2)}MB)` : ''}
                  </p>
                </div>
              )}
            </div>
            
            <div className="save-button-container" style={{ marginTop: '30px' }}>
              <button 
                type="submit" 
                className="save-slideshow-btn"
                disabled={isLoading || (!editingSlide && !image)}
                style={{
                  background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
                  color: 'white',
                  fontSize: '1.3rem',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  fontWeight: '700',
                  boxShadow: '0 8px 20px rgba(56, 239, 125, 0.25)',
                  border: 'none',
                  width: '100%',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  marginBottom: '15px'
                }}
              >
                {isLoading ? 'Saving...' : 'SAVE SLIDESHOW'}
                <span style={{ 
                  position: 'absolute',
                  right: '20px',
                  fontSize: '1.5rem'
                }}>
                  💾
                </span>
              </button>
              
              <button 
                type="button" 
                className="cancel-btn"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      <div className="slides-list">
        <h3>Existing Slides</h3>
        
        {slides.length === 0 ? (
          <p className="no-slides-message">No slideshow images available. Add your first slide!</p>
        ) : (
          <div className="slides-grid">
            {slides.map((slide) => (
              <div key={slide.id} className={`slide-item ${!slide.active ? 'inactive' : ''}`}>
                <div className="slide-image-container">
                  <img 
                    src={slide.imageBase64} 
                    alt={slide.title || 'Slideshow image'} 
                    className="slide-image"
                  />
                  <div className="slide-number">{slide.order + 1}</div>
                </div>
                
                <div className="slide-details">
                  <h4 className="slide-title">{slide.title || 'Untitled Slide'}</h4>
                  {slide.description && (
                    <p className="slide-description">{slide.description}</p>
                  )}
                </div>
                
                <div className="slide-actions">
                  <button 
                    className="edit-btn"
                    onClick={() => handleEditClick(slide)}
                    disabled={isLoading}
                  >
                    Edit
                  </button>
                  <button
                    className={`toggle-btn ${slide.active ? 'deactivate' : 'activate'}`}
                    onClick={() => handleToggleActive(slide)}
                    disabled={isLoading}
                  >
                    {slide.active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteClick(slide)}
                    disabled={isLoading}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SlideshowManager; 